/**
 * Tauri API Adapter
 * 
 * Provides a unified interface for Tauri commands that mirrors the desktop API structure.
 * This allows gradual migration from legacy desktop IPC to Tauri.
 */

import { isTauriMode } from './environmentDetector';

// Dynamic import for Tauri - only loads when in Tauri environment
let tauriInvoke: ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null = null;

/**
 * Initialize Tauri API (call once on app startup)
 */
export async function initTauriAPI(): Promise<boolean> {
    if (!isTauriMode()) {
        console.log('[TauriAPI] Not in Tauri mode, skipping initialization');
        return false;
    }

    try {
        const { invoke } = await import('@tauri-apps/api/core');
        tauriInvoke = invoke;
        console.log('[TauriAPI] Initialized successfully');
        return true;
    } catch (error) {
        console.error('[TauriAPI] Failed to initialize:', error);
        return false;
    }
}

/**
 * Invoke a Tauri command
 */
export async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
    if (!tauriInvoke) {
        throw new Error('[TauriAPI] Not initialized. Call initTauriAPI() first.');
    }
    return tauriInvoke(cmd, args) as Promise<T>;
}

/**
 * Check if Tauri API is ready
 */
export function isReady(): boolean {
    return tauriInvoke !== null;
}

// ============================================================================
// Master Data Commands
// ============================================================================

export interface MasterDataDoc {
    id: string;
    store_id: string;
    data: unknown;
    updated_at: number;
    synced: boolean;
}

export const masterData = {
    async findAll(collection: string): Promise<MasterDataDoc[]> {
        return invoke<MasterDataDoc[]>('find_all', { collection });
    },

    async findOne(collection: string, docId: string): Promise<MasterDataDoc | null> {
        return invoke<MasterDataDoc | null>('find_one', { collection, docId });
    },

    async findByStore(collection: string, storeId: string): Promise<MasterDataDoc[]> {
        return invoke<MasterDataDoc[]>('find_by_store', { collection, storeId });
    },

    async insert(collection: string, docId: string, storeId: string, data: unknown): Promise<void> {
        return invoke('insert', { collection, docId, storeId, data });
    },

    async remove(collection: string, docId: string): Promise<boolean> {
        return invoke<boolean>('remove', { collection, docId });
    },

    async clear(collection: string): Promise<number> {
        return invoke<number>('clear', { collection });
    },

    async getSyncStatus(): Promise<Record<string, { count: number; last_updated: number | null }>> {
        return invoke('get_sync_status');
    },
};

// ============================================================================
// Certificate Commands
// ============================================================================

export interface CsrData {
    common_name: string;        // CN = userId
    organization: string;       // O = sectorCode-authCode
    organizational_unit?: string;  // OU = qstNumber
    locality?: string;          // L = timezone
    state?: string;             // ST = province
    country: string;            // C = country code
    email?: string;
    surname?: string;           // SN = nickname (required by MEV)
    given_name?: string;        // GN = dossierNumber (required by MEV)
}

export interface CsrResult {
    csr_pem: string;
    private_key_pem: string;
    public_key_pem: string;
}

export interface CertificateInfo {
    subject: string;
    issuer: string;
    serial_number: string;
    not_before: string;
    not_after: string;
    fingerprint_sha1: string;
    fingerprint_sha256: string;
}

export const certificate = {
    async generateCsr(csrData: CsrData): Promise<CsrResult> {
        return invoke<CsrResult>('generate_csr', { csrData });
    },

    async parseCertificate(certPem: string): Promise<CertificateInfo> {
        return invoke<CertificateInfo>('parse_certificate', { certPem });
    },

    async signData(data: string, privateKeyPem: string): Promise<string> {
        return invoke<string>('sign_data', { data, privateKeyPem });
    },

    async calculateFingerprint(pem: string): Promise<string> {
        return invoke<string>('calculate_fingerprint', { pem });
    },

    async extractPublicKey(privateKeyPem: string): Promise<string> {
        return invoke<string>('extract_public_key', { privateKeyPem });
    },
};

// ============================================================================
// Windows Certificate Store Commands
// ============================================================================

export interface CertificateEntry {
    thumbprint: string;
    subject: string;
    friendly_name: string;
    not_before: string;
    not_after: string;
    has_private_key: boolean;
}

export const windowsStore = {
    async listMevCertificates(): Promise<CertificateEntry[]> {
        return invoke<CertificateEntry[]>('list_mev_certificates');
    },

    async storeCertificate(
        certPem: string,
        privateKeyPem: string | null,
        friendlyName: string
    ): Promise<string> {
        return invoke<string>('store_certificate', { certPem, privateKeyPem, friendlyName });
    },

    async getCertificateAndKey(thumbprint: string): Promise<[string, string | null]> {
        return invoke<[string, string | null]>('get_certificate_and_key', { thumbprint });
    },

    async deleteCertificate(thumbprint: string): Promise<boolean> {
        return invoke<boolean>('delete_certificate', { thumbprint });
    },
};

// ============================================================================
// Printing Commands
// ============================================================================

export const printing = {
    async printNetwork(address: string, port: number, data: Uint8Array): Promise<void> {
        return invoke('print_network', { address, port, data: Array.from(data) });
    },

    async printSerial(name: string, data: Uint8Array): Promise<void> {
        return invoke('print_serial', { name, data: Array.from(data) });
    },

    async checkPrinterStatus(identifier: string, port?: number): Promise<boolean> {
        return invoke<boolean>('check_printer_status', { identifier, port });
    },

    async openCashDrawer(
        printerType: 'network' | 'serial' | 'shared',
        printerIdentifier: string,
        port?: number
    ): Promise<void> {
        return invoke('open_cash_drawer', { printerType, printerIdentifier, port });
    },
};

// ============================================================================
// Printer Management Commands
// ============================================================================

export interface PrinterItem {
    _id?: string;
    name: string;
    api_key: string;
    printer_type: string;
    windows_shared_name?: string;
    network_address?: string;
    network_port?: number;
    num_copies?: number;
    print_qr_receipt?: boolean;
    command_language?: 'ESCPOS' | 'STARLINE' | 'STARGRAPHIC' | 'STARPRNT' | 'STARIMPACT' | string;
    command_language_verified?: 'ESCPOS' | 'STARLINE' | 'STARGRAPHIC' | 'STARPRNT' | 'STARIMPACT' | string;
    printer_model?: string;
    location?: string;
}

const printerListCache = new Map<string, Promise<PrinterItem[]>>();

function clearPrinterListCache() {
    printerListCache.clear();
}

export const printerManagement = {
    async findPrinters(storeId?: string): Promise<PrinterItem[]> {
        const key = String(storeId || '').trim();
        if (!key) return [];
        let pending = printerListCache.get(key);
        if (!pending) {
            pending = invoke<PrinterItem[]>('find_printers', { storeId: key }).catch((error) => {
                printerListCache.delete(key);
                throw error;
            });
            printerListCache.set(key, pending);
        }
        const printers = await pending;
        return printers.map((printer) => ({ ...printer }));
    },

    async findPrinterById(id: string): Promise<PrinterItem | null> {
        return invoke<PrinterItem | null>('find_printer_by_id', { id });
    },

    async insertPrinter(printer: PrinterItem, storeId?: string): Promise<PrinterItem> {
        const saved = await invoke<PrinterItem>('insert_printer', { printer, storeId: String(storeId || '').trim() });
        clearPrinterListCache();
        return saved;
    },

    async updatePrinter(printer: PrinterItem, storeId?: string): Promise<PrinterItem> {
        const saved = await invoke<PrinterItem>('update_printer', { printer, storeId: String(storeId || '').trim() });
        clearPrinterListCache();
        return saved;
    },

    async removePrinter(id: string): Promise<boolean> {
        const removed = await invoke<boolean>('remove_printer', { id });
        clearPrinterListCache();
        return removed;
    },

    async syncPrintersFromCloud(printers: PrinterItem[], storeId?: string): Promise<PrinterItem[]> {
        const key = String(storeId || '').trim();
        const saved = await invoke<PrinterItem[]>('sync_printers_from_cloud', { printers, storeId: key });
        if (key) printerListCache.set(key, Promise.resolve(saved));
        return saved;
    },
};

export type DiscoveredPrinterStatus = 'ready' | 'error' | 'unreachable' | 'unknown';

export interface DiscoveredPrinter {
    id: string;
    name: string;
    ip_address?: string | null;
    port?: number | null;
    protocol: string;
    status: DiscoveredPrinterStatus | string;
    last_check: string;
    source: string;
    windows_name?: string | null;
    driver_name?: string | null;
}

export const printerDiscovery = {
    async scanPrinters(): Promise<DiscoveredPrinter[]> {
        return invoke<DiscoveredPrinter[]>('scan_printers');
    },

    async cancelScanPrinters(): Promise<void> {
        return invoke('cancel_scan_printers');
    },

    async addPrinterByIp(ipAddress: string, port?: number): Promise<DiscoveredPrinter> {
        return invoke<DiscoveredPrinter>('add_printer_by_ip', {
            input: { ip_address: ipAddress, port },
        });
    },

    async checkDiscoveredPrinters(printers: DiscoveredPrinter[]): Promise<DiscoveredPrinter[]> {
        return invoke<DiscoveredPrinter[]>('check_discovered_printers', { printers });
    },

    async printTestPage(printer: DiscoveredPrinter): Promise<void> {
        return invoke('print_test_page', { printer });
    },
};

// ============================================================================
// System Commands
// ============================================================================

export const receipt = {
    async pickFolder(defaultPath?: string): Promise<string | null> {
        return invoke<string | null>('pick_folder', { defaultPath: defaultPath || null });
    },

    async saveReceiptImage(filePath: string, base64Data: string): Promise<boolean> {
        return invoke<boolean>('save_receipt_image', { filePath, base64Data });
    },
};

export interface StationHardware {
    motherboardSerial: string;
    biosUuid: string;
    fingerprint: string;
    computerName?: string;
    windowsVersion?: string;
    manufacturer?: string;
    model?: string;
    processor?: string;
    memory?: string;
    localIp?: string;
}

export const system = {
    async getStationFingerprint(): Promise<StationHardware> {
        return invoke<StationHardware>('get_station_fingerprint');
    },

    async getNetworkStatus(): Promise<boolean> {
        return invoke<boolean>('get_network_status');
    },

    async getAppVersion(): Promise<string> {
        return invoke<string>('get_app_version');
    },

    async saveStationConnectionSecret(storeId: string, fingerprint: string, secret: string): Promise<void> {
        return invoke('save_station_connection_secret', { storeId, fingerprint, secret });
    },

    async readStationConnectionSecret(): Promise<{ stores: Record<string, { fingerprint: string; secret: string }> } | null> {
        return invoke('read_station_connection_secret');
    },

    async removeStationConnectionSecret(storeId: string): Promise<void> {
        return invoke('remove_station_connection_secret', { storeId });
    },

    async clearStationConnectionSecret(): Promise<void> {
        return invoke('clear_station_connection_secret');
    },

    async lanSyncStart(role: string, storeId: string, stationId: string, stationName: string, channels: string[]): Promise<void> {
        return invoke('lan_sync_start', { role, storeId, stationId, stationName, channels });
    },

    async lanSyncSubmit(operation: {
        operationId: string;
        storeId: string;
        terminalId: string;
        channel: string;
        entityId: string;
        operationType: string;
        payload: unknown;
    }): Promise<{ messageType?: string; operationId?: string; serverSequence?: number; reason?: string }> {
        return invoke('lan_sync_submit', { operation });
    },

    async lanSyncPendingCount(storeId: string): Promise<number> {
        return invoke('lan_sync_pending_count', { storeId });
    },
};

// ============================================================================
// Export all as tauriAPI namespace
// ============================================================================

export const tauriAPI = {
    init: initTauriAPI,
    invoke,
    isReady,
    masterData,
    certificate,
    windowsStore,
    printing,
    printerManagement,
    printerDiscovery,
    receipt,
    system,
};

export default tauriAPI;
